import { useCallback, useEffect, useRef } from 'react';
import { findIndex, includes } from 'lodash-es';

import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { fireStepSubmittedWithErrorsEvent } from 'client/engagement-handlers/appraisal-engagement-handler/appraisal-engagement-handler';
import {
  LOCATION_AND_STYLE,
  CONDITION_AND_MILEAGE,
  REPORT,
  PARTNER_FAQ_VISIBLE_BY_APPRAISER_STEP_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { isLowValueOption } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/colors-and-options-step/colors-and-options-step-utils';
import {
  fireActiveStepEvents,
  fireActiveStepEndEvents,
  fireActiveStepEndInvalidatedEvents,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-tracking-utils';
import {
  getValidationErrors,
  revalidateErrors,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-validators';
import { processStepsUpdate } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-step-processor';
import { getPredefinedVin } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';

export const EMO_4318_NEXT_STEP_SCROLL_PAUSE = 500;

/**
 * Encapsulates step navigation — toggleStepDone and changeStep.
 * Owns the logic for updating steps and errorMap in state.
 *
 * @param {Object} params
 * @param {Function} params.setState - state setter
 * @param {Function} params.getVehicleStyle - returns current vehicle style slug
 * @param {Function} params.getVehicleYear - returns current vehicle year
 * @param {Function} params.getActiveStep - returns the active step object
 * @param {boolean} params.isExtendedReport - partner-offer flow flag
 * @param {boolean} params.isMobile - mobile viewport flag
 * @param {Object} params.location - current page location
 * @param {Function} params.updateAllTmvParams - triggers TMV re-fetch
 * @param {Function} params.updateOfferParametersVin - syncs VIN to partner offer model
 * @param {Function} params.onEstimatedValuesWidgetHide - callback to hide estimated values widget
 * @param {Function} params.onLinkWidgetHide - callback to hide link widget
 * @param {Function} params.onPartnerFaqQuestionsShow - callback to show/hide partner FAQ
 * @param {Object} params.pendingCallbacksRef - ref queue for post-commit callbacks
 * @param {Object} params.changeStepRef - ref to changeStep (kept current for circular-dep avoidance)
 * @param {Function} params.debouncedScrollToStep - debounced scroll to step
 * @param {Function} params.smoothScrollToStep - challenge scroll with an optional completion callback
 * @param {Function} params.scrollToReportStep - scroll to report step
 * @param {Function} params.scrollToTradeInTitle - scroll to trade-in title
 * @param {Function} params.updateErrorMapAndScrollToInvalid - updates errorMap and scrolls to first error
 * @param {Object} params.stateRef - ref always pointing at the current state (avoids state in dep arrays)
 * @param {boolean} params.isEmo4318Chal1 - whether to sequence forward navigation after the current collapse closes
 * @param {Function} params.setTransitioningStepId - marks the step whose collapse is closing
 * @param {Function} params.setIsNextStepOpenDelayed - delays opening until challenge scrolling completes
 */
export function useStepNavigation({
  setState,
  getVehicleStyle,
  getVehicleYear,
  getActiveStep,
  isExtendedReport,
  isMobile,
  location,
  updateAllTmvParams,
  updateOfferParametersVin,
  onEstimatedValuesWidgetHide,
  onLinkWidgetHide,
  onPartnerFaqQuestionsShow,
  pendingCallbacksRef,
  debouncedScrollToStep,
  smoothScrollToStep,
  scrollToReportStep,
  scrollToTradeInTitle,
  updateErrorMapAndScrollToInvalid,
  stateRef,
  isEmo4318Chal1,
  setTransitioningStepId,
  setIsNextStepOpenDelayed,
}) {
  const pendingStepChangeRef = useRef(null);
  const nextStepScrollTimerRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(nextStepScrollTimerRef.current);
    },
    []
  );

  const onStepCollapseComplete = useCallback(
    stepId => {
      if (pendingStepChangeRef.current?.stepId === stepId) {
        const { commitStepChange } = pendingStepChangeRef.current;
        pendingStepChangeRef.current = null;
        setTransitioningStepId(null);
        setIsNextStepOpenDelayed(true);
        commitStepChange();
      }
    },
    [setIsNextStepOpenDelayed, setTransitioningStepId]
  );

  const toggleStepDone = useCallback(
    (isStepValid, stepId, skipStep = false, resetSubsequentSteps = true) => {
      setState(prev => {
        const steps = [...prev.steps];
        const index = findIndex(steps, { id: stepId });
        const nextIndex = index + 1;
        const stepsLength = steps.length;

        steps[index] = { ...steps[index], isDone: isStepValid, skipStep };

        if (resetSubsequentSteps) {
          for (let i = nextIndex; i < stepsLength; i += 1) {
            steps[i] = { ...steps[i], isDone: false };
          }
        }

        return revalidateErrors(stepId, { ...prev, steps }, isExtendedReport);
      });
    },
    [setState, isExtendedReport]
  );

  const changeStep = useCallback(
    ({ currentTarget, stepId: altStepId, creativeId: altCreativeId }) => {
      delayHeavyTask(() => {
        const {
          condition,
          mileage,
          vin,
          zipCode,
          transmissionType,
          isTransmissionTypeSelectedByUser,
          exteriorColor,
          interiorColor,
          options,
          optionsCount,
        } = stateRef.current;
        const style = getVehicleStyle();
        const vehicleYear = getVehicleYear();
        const exteriorColorName = exteriorColor?.name ?? '';
        const interiorColorName = interiorColor?.name ?? '';
        const selectedOptionsCount = options.length;
        const selectedLowValueOptionsCount = options.filter(isLowValueOption).length;
        const activeStep = getActiveStep();
        const activeStepId = activeStep.id;
        let selectedStepId = altStepId;

        if (currentTarget) {
          selectedStepId = currentTarget.getAttribute('data-index');
        }

        const skipEmptyStep = !!activeStep.skipStep;
        const userSelectedPreviousStep = activeStep.prevIds.includes(selectedStepId);
        const selectedStep = stateRef.current.steps.find(({ id }) => id === selectedStepId);
        const isNavigatingBetweenDoneSteps = activeStep.isDone && selectedStep?.isDone;
        const validationErrors = getValidationErrors(activeStepId, stateRef.current, isExtendedReport);
        const creativeId = currentTarget ? getCreativeId(currentTarget) : altCreativeId;

        if (userSelectedPreviousStep) {
          clearTimeout(nextStepScrollTimerRef.current);
          nextStepScrollTimerRef.current = null;
          setIsNextStepOpenDelayed(false);
          if (pendingStepChangeRef.current) {
            pendingStepChangeRef.current = null;
            setTransitioningStepId(null);
          }
        }

        if (skipEmptyStep || userSelectedPreviousStep || !validationErrors.size) {
          const stepIdToBeActive = selectedStepId;

          if (activeStepId === LOCATION_AND_STYLE) {
            if (onEstimatedValuesWidgetHide) {
              onEstimatedValuesWidgetHide();
            }
            onLinkWidgetHide();
          }

          if (activeStepId === CONDITION_AND_MILEAGE && !userSelectedPreviousStep) {
            updateAllTmvParams();
            if (isExtendedReport) {
              updateOfferParametersVin();
            }
          }

          fireActiveStepEvents(stepIdToBeActive);
          fireActiveStepEndEvents(activeStepId, {
            style,
            exteriorColor,
            interiorColor,
            exteriorColorName,
            interiorColorName,
            selectedOptionsCount,
            selectedLowValueOptionsCount,
            optionsCount,
            condition,
            mileage,
            creativeId,
            vin,
            userSelectedPreviousStep,
            zipCode,
            vehicleYear,
            transmissionType,
            isTransmissionTypeSelectedByUser,
            location,
          });

          const commitStepChange = () =>
            setState(prev => {
              const { steps, hasVinMismatchError, isVinFromDrawer } = prev;
              const { vin: queryVin } = getPredefinedVin(location);

              const updatedSteps = processStepsUpdate(steps, activeStepId, stepIdToBeActive, userSelectedPreviousStep, {
                hasVinMismatchError,
                isVinFromDrawer,
                queryVin,
              });

              const newActiveStep = updatedSteps.find(s => s.isActive);
              const newStepId = newActiveStep?.id;

              pendingCallbacksRef.current.push(() => {
                if (isExtendedReport) {
                  if (isEmo4318Chal1 && !userSelectedPreviousStep && !isNavigatingBetweenDoneSteps) {
                    clearTimeout(nextStepScrollTimerRef.current);
                    nextStepScrollTimerRef.current = setTimeout(() => {
                      nextStepScrollTimerRef.current = null;
                      smoothScrollToStep(newStepId, () => setIsNextStepOpenDelayed(false));
                    }, EMO_4318_NEXT_STEP_SCROLL_PAUSE);
                  } else {
                    debouncedScrollToStep(newStepId);
                  }
                } else if (isMobile && newStepId === REPORT) {
                  scrollToReportStep();
                } else {
                  scrollToTradeInTitle();
                }
                onPartnerFaqQuestionsShow(
                  isExtendedReport && includes(PARTNER_FAQ_VISIBLE_BY_APPRAISER_STEP_ID, newStepId)
                );
              });

              return {
                ...prev,
                steps: updatedSteps,
                errorMap: new Map(),
              };
            });

          if (
            isEmo4318Chal1 &&
            !userSelectedPreviousStep &&
            !isNavigatingBetweenDoneSteps &&
            activeStepId !== stepIdToBeActive
          ) {
            setTransitioningStepId(activeStepId);
            pendingStepChangeRef.current = { stepId: activeStepId, commitStepChange };
            smoothScrollToStep(activeStepId);
          } else {
            commitStepChange();
          }
        } else {
          updateErrorMapAndScrollToInvalid(validationErrors);
          fireStepSubmittedWithErrorsEvent({ stepId: activeStepId, errorMap: validationErrors });
          fireActiveStepEndInvalidatedEvents(activeStepId, { creativeId });
        }
      });
    },
    [
      stateRef,
      getVehicleStyle,
      getVehicleYear,
      getActiveStep,
      location,
      setState,
      onEstimatedValuesWidgetHide,
      onLinkWidgetHide,
      updateAllTmvParams,
      isExtendedReport,
      updateOfferParametersVin,
      pendingCallbacksRef,
      isMobile,
      onPartnerFaqQuestionsShow,
      debouncedScrollToStep,
      smoothScrollToStep,
      scrollToReportStep,
      scrollToTradeInTitle,
      updateErrorMapAndScrollToInvalid,
      isEmo4318Chal1,
      setTransitioningStepId,
      setIsNextStepOpenDelayed,
    ]
  );

  return { toggleStepDone, changeStep, onStepCollapseComplete };
}
